import React from 'react'
import Reflux from 'reflux-react-16'
import _ from 'lodash'
import {Route} from 'react-router-dom'

import LoginStore from '../../stores/LoginStore.js'
import LoginActions from '../../actions/LoginActions.js'
import {getAvailableBranches} from '../../utils/branches.js'
import {sanitizeRedirect} from '../../utils/redirect.js'

import {Colors, Popup, Stack} from '../../components/UI/index.js'
import AppNav, {layoutStyle, layoutContentStyle} from '../../components/navigation/AppNav.js'
import {getNavPosition, subscribeNavPosition} from '../../components/navigation/navPosition.js'
import {plannerNavigation} from '../../components/navigation/plannerNavigation.js'
import LoginPopup from '../../components/login/LoginPopup.js'
import {openMfaPopup} from '../../components/login/mfaPopup.js'
import showTwoFactorWarning from '../../components/login/twoFactorWarning.js'
import Dashboard from './Dashboard.js'
import Packages from './Packages.js'
import Parcels from './Parcels.js'
import Shifts from './Shifts.js'
import ShiftTemplates from './ShiftTemplates.js'
import Customers from './Customers.js'
import Users from './Users.js'
import Invoices from './Invoices.js'
import Subscriptions from './Subscriptions.js'
import Settings from './settings/Settings.js'
import NotificationsPopup from '../../components/communication/NotificationsPopup.js'
import NotificationsPage from '../../components/communication/NotificationsPage.js'
import AddressBook from './AddressBook.js'
import Vehicles from './Vehicles.js'
import Analytics from './Analytics.js'
import Routes, {routesTableEnabled} from './Routes.js'
import AccountModals, {canEditOwnAccount} from '../../components/account/AccountModals.js'

// The account menu's own details and password (#602) through this app's session
const accountApi = {
    getOwnDetails: (callback) => LoginActions.getOwnDetails(callback),
    updateOwnDetails: (details, callback) => LoginActions.updateOwnDetails(details, callback),
    changePassword: (currentPassword, newPassword, callback) => LoginActions.changePassword(currentPassword, newPassword, callback)
}

export default class Layout extends Reflux.Component {
    constructor(props) {
        super(props)

        this.store = LoginStore
    }

    componentDidMount() {
        const {history} = this.props

        LoginActions.loggedIn((loggedIn) => {
            if (!loggedIn) {
                const redirect = sanitizeRedirect(`${history.location.pathname}${history.location.search}`)

                if (redirect) {
                    history.push(`/login?redirect=${encodeURIComponent(redirect)}`)
                } else {
                    history.push('/login')
                }
            } else {
                if (history.location.pathname === '/') {
                    if (this.state.user?.isCustomer) {
                        history.push('/klant')
                    } else {
                        history.push('/planning')
                    }
                }

                LoginActions.checkNotifications(() => {
                    this.notificationsPopup?.checkIfNewNotification()
                })
            }
        })

        this.unsubscribeNavPosition = subscribeNavPosition(() => this.setState({navPosition: getNavPosition()}))
    }

    componentWillUnmount() {
        super.componentWillUnmount()
        this.unsubscribeNavPosition?.()
    }

    componentDidUpdate(prevProps, prevState) {
        const {history} = this.props

        if (prevState.user !== this.state.user) {
            if (this.state.user?.isCustomer) {
                history.push('/klant/')
            }
        }
    }

    onClickHelp() {
        const {branding} = this.state

        window.open((branding?.manualURLReseller) || 'https://handleiding.veloyd.nl/docs/vervoerder/', '_blank')
    }

    onChangeSelectedBranches(selectedBranchIds) {
        LoginActions.setSelectedBranches(selectedBranchIds)
        window.location.reload()
    }

    onClickSwitchAccount() {
        const {accounts, user} = this.state

        this.loginPopup.open(accounts, user, (index) => {
            const account = accounts[index]

            if ((account.mfaRequired && !user.is2FASignedIn)) {
                if (account.mfaEnabled) {
                    if (account.mfaMethod === 'email') {
                        LoginActions.mailMFACode(null, index, null, (err) => {
                            if (err) {
                                this.popup.setError(err)
                            }
                        })
                    }

                    openMfaPopup(this.popup, {
                        mfaMethod: account.mfaMethod,
                        onLogin: (code) => {
                            LoginActions.switchAccount(index, code, (err) => {
                                if (err) {
                                    this.popup.setError(err)
                                }
                            })
                        }
                    })
                } else {
                    showTwoFactorWarning(this.popup, () => LoginActions.logout())
                }
            } else {
                LoginActions.switchAccount(index, null, (err, reseller) => {
                    if (err) {
                        this.setState({loading: false, error: err})

                        if ((!account.isMessenger && reseller.settings.mfaWebEnabled) || (account.isMessenger && reseller.settings.mfaAppEnabled)) {
                            showTwoFactorWarning(this.popup, () => LoginActions.logout())
                        }
                    }
                })
            }
        })
    }

    onClickLogout() {
        this.popup.open('Uitloggen', 'Weet je zeker dat je wilt uitloggen?', () => LoginActions.logout())
    }

    updateReseller(reseller, path, unsetPath) {
        this.setState({reseller: {...reseller}, saved: false}, () => {
            if (path) {
                const value = _.get(reseller.settings, path)

                LoginActions.updateResellerSettings(path, value, unsetPath, (err) => {
                    if (!err) {
                        clearTimeout(this.state.timeout)
                        const timeout = setTimeout(() => this.setState({saved: false}), 3000)
                        this.setState({saved: true, timeout})
                    } else {
                        this.setState({error: err})
                    }
                })
            }
        })
    }

    render() {
        const {reseller, user, accounts, notifications, branding, selectedBranchIds} = this.state
        const {history} = this.props
        const {pathname} = history.location

        // important
        user.permissions = user.permissions || {}

        const navigation = plannerNavigation({
            reseller,
            user,
            accounts,
            pathname,
            search: history.location.search,
            routesTable: routesTableEnabled(),
            availableBranches: getAvailableBranches(reseller, user),
            selectedBranchIds,
            onChangeSelectedBranches: this.onChangeSelectedBranches.bind(this),
            onClickHelp: this.onClickHelp.bind(this),
            onClickSwitchAccount: this.onClickSwitchAccount.bind(this),
            onClickLogout: this.onClickLogout.bind(this),
            ...canEditOwnAccount(user) && {
                onClickDetails: () => this.accountModals?.openDetails(),
                onClickPassword: () => this.accountModals?.openPassword()
            }
        })

        const navPosition = getNavPosition()

        return (
            <Stack style={{flex: 1, minWidth: 1366, height: '100vh', overflow: 'hidden', ...layoutStyle(navPosition)}}>
                <AppNav navigation={navigation} position={navPosition} branding={branding} history={history} />

                <Stack style={{flex: 1, background: Colors.backgroundNeutral, ...layoutContentStyle(navPosition)}}>
                    {reseller.permissions.orders && user.permissions.orders &&
                        <>
                            <Route path='/planning/:orderId?' render={(props) => <Dashboard {...props} user={user} reseller={reseller} selectedBranchIds={selectedBranchIds} onUpdateReseller={this.updateReseller.bind(this)} />} />
                            <Route path='/emballage/:id?' render={(props) => <Packages {...props} user={user} reseller={reseller} />} />
                            {(reseller.accountType === 'carrierPlus' || routesTableEnabled()) &&
                                <Route path='/routes' render={(props) => <Routes {...props} user={user} reseller={reseller} onUpdateReseller={this.updateReseller.bind(this)} />} />
                            }
                        </>
                    }

                    {reseller.permissions.parcels && user.permissions.parcels &&
                        <Route path='/zendingen/:parcelId?' render={(props) => <Parcels {...props} user={user} reseller={reseller} />} />
                    }

                    {reseller.permissions.analytics && user.permissions.analytics &&
                        <Route path='/rapportage' render={(props) => <Analytics {...props} user={user} reseller={reseller} history={history} branding={branding} />} />
                    }

                    {reseller.permissions.shifts && user.permissions.shifts &&
                        <Route path='/diensten/:shiftId?' render={(props) => <Shifts {...props} user={user} reseller={reseller} />} />
                    }

                    {reseller.permissions.shifts && user.permissions.shifts &&
                        <Route path='/diensttemplates' render={(props) => <ShiftTemplates {...props} user={user} reseller={reseller} />} />
                    }

                    {reseller.permissions.customers && user.permissions.customers &&
                        <Route path='/klanten' render={(props) => <Customers {...props} user={user} reseller={reseller} />} />
                    }
                    {reseller.permissions.users && user.permissions.users &&
                        <Route path='/gebruikers' render={(props) => <Users {...props} user={user} reseller={reseller} />} />
                    }
                    {reseller.permissions.vehicles && user.permissions.vehicles &&
                        <Route path='/voertuigen' render={(props) => <Vehicles {...props} user={user} reseller={reseller} />} />
                    }
                    {reseller.permissions.invoices && user.permissions.invoices &&
                        <Route path='/facturatie' render={(props) => <Invoices {...props} user={user} reseller={reseller} onUpdateReseller={this.updateReseller.bind(this)} />} />
                    }
                    {(Object.keys(reseller.permissions).some((key) => reseller.permissions[key]) && Object.keys(user.permissions).some((key) => user.permissions[key])) &&
                        <Route path='/adresboek' render={(props) => <AddressBook {...props} user={user} reseller={reseller} />} />
                    }

                    {reseller.permissions.subscriptions && user.permissions.subscriptions &&
                        <Route path='/abonnementen' render={(props) => <Subscriptions {...props} user={user} reseller={reseller} />} />
                    }
                    <Route path='/meldingen' render={(props) => <NotificationsPage {...props} showList user={user} notifications={notifications} reseller={reseller} />} />

                    {user.permissions.settings &&
                        <Route path='/instellingen' render={(props) => <Settings {...props} user={user} reseller={reseller} />} />
                    }
                </Stack>

                <LoginPopup ref={(ref) => this.loginPopup = ref} />
                <Popup ref={(ref) => this.popup = ref} />
                <AccountModals api={accountApi} ref={(ref) => this.accountModals = ref} />

                <NotificationsPopup
                    user={user}
                    onClickReadNotifications={LoginActions.updateReadNotifications}
                    notifications={notifications}
                    ref={(ref) => this.notificationsPopup = ref}
                />
            </Stack>
        )
    }
}
